import React, { useEffect, useRef } from "react";
import { UseSimplePinchZoomOptions } from "./types";

export const useSimplePinchZoom = (
    ref: React.RefObject<HTMLElement>,
    { minScale = 1, maxScale = 3 }: UseSimplePinchZoomOptions = {}
) => {
    const scaleRef = useRef(1);
    const lastDistanceRef = useRef<number | null>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        el.style.willChange = "transform";
        el.style.touchAction = "none";

        const getDistance = (touches: TouchList) => {
            const [a, b] = [touches[0], touches[1]];
            return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
        };

        const onTouchStart = (e: TouchEvent) => {
            if (e.touches.length === 2) {
                lastDistanceRef.current = getDistance(e.touches);
            }
        };

        const onTouchMove = (e: TouchEvent) => {
            if (e.touches.length !== 2) return;

            e.preventDefault();
            const newDistance = getDistance(e.touches);
            const oldDistance = lastDistanceRef.current;
            if (!oldDistance) return;

            let newScale = scaleRef.current * (newDistance / oldDistance);
            newScale = Math.min(Math.max(newScale, minScale), maxScale);
            scaleRef.current = newScale;

            el.style.transform = `scale(${newScale})`;
            lastDistanceRef.current = newDistance;
        };

        const onTouchEnd = () => {
            scaleRef.current = 1;
            lastDistanceRef.current = null;
            el.style.transform = "none";
        };

        el.addEventListener("touchstart", onTouchStart, { passive: false });
        el.addEventListener("touchmove", onTouchMove, { passive: false });
        el.addEventListener("touchend", onTouchEnd);
        el.addEventListener("touchcancel", onTouchEnd);

        return () => {
            el.removeEventListener("touchstart", onTouchStart);
            el.removeEventListener("touchmove", onTouchMove);
            el.removeEventListener("touchend", onTouchEnd);
            el.removeEventListener("touchcancel", onTouchEnd);
        };
    }, [ref, minScale, maxScale]);
};